/**
 * Each ambassador's Skida Premium — 27 Oct 2026.
 *
 * Grants are made by hand in RevenueCat, a year at a time, and run out without
 * telling anybody. So the ambassador record is linked to its app account — the
 * email looked up among the product's users, here, because staff can read them
 * and the engine's account cannot — and `crmLinkAmbassador` records it for RevenueCat to be asked
 * what that account has and when it ends. See
 * `functions-webhook/src/ambassadorAccess.ts`.
 */
import { collection, getDocs, onSnapshot, query, where } from 'firebase/firestore'
import { db, productDb } from '@/shared/firebase/client'
import { CRM_COLLECTIONS, PRODUCT_COLLECTIONS } from '@/shared/firebase/collections'
import { callFunction } from '@/shared/firebase/callFunction'

export type Premium = 'grant' | 'lifetime' | 'subscription' | 'none' | 'no-account'

export interface AmbassadorAccess {
  personId: string
  /** The email the link was made from; a changed email is looked up again. */
  email: string
  uid: string | null
  uids: string[]
  manual: boolean
  premium: Premium
  untilMs: number | null
  project: string
  checkedAt: Date | null
  error: string | null
  /** Waiting for RevenueCat — answered within two minutes. */
  pending: boolean
}

export function subscribeAmbassadorAccess(onData: (byPerson: Map<string, AmbassadorAccess>) => void): () => void {
  return onSnapshot(collection(db, CRM_COLLECTIONS.ambassadorAccess), (snap) => {
    const out = new Map<string, AmbassadorAccess>()
    for (const d of snap.docs) {
      const v = d.data()
      out.set(d.id, {
        personId: d.id,
        email: typeof v.email === 'string' ? v.email : '',
        uid: typeof v.uid === 'string' ? v.uid : null,
        uids: Array.isArray(v.uids) ? v.uids.map(String) : [],
        manual: v.manual === true,
        premium: (v.premium ?? 'none') as Premium,
        untilMs: typeof v.untilMs === 'number' ? v.untilMs : null,
        project: typeof v.project === 'string' ? v.project : '',
        checkedAt: v.checkedAt?.toDate?.() ?? null,
        error: typeof v.error === 'string' ? v.error : null,
        pending: v.pending === true,
      })
    }
    onData(out)
  })
}

/** The app accounts holding this email, as typed and in lower case. */
export async function findAppUids(email: string): Promise<string[]> {
  const e = email.trim()
  if (!e) return []
  const variants = [...new Set([e, e.toLowerCase()])]
  const snap = await getDocs(
    query(collection(productDb, PRODUCT_COLLECTIONS.users), where('email', 'in', variants)),
  )
  return [...new Set(snap.docs.map((d) => d.id))]
}

/** Link (or relink) an ambassador and have RevenueCat checked now. */
export async function checkAccess(personId: string, email: string, uids: string[], manual = false): Promise<void> {
  await callFunction('crmLinkAmbassador', {
    method: 'POST',
    body: { personId, email: email.trim().toLowerCase(), uids, manual },
  })
}

/** Find the account by email, then check it. */
export async function linkByEmail(personId: string, email: string): Promise<void> {
  await checkAccess(personId, email, await findAppUids(email))
}

/**
 * The customer in RevenueCat's dashboard. The API's project id carries a
 * `proj` prefix the dashboard's address does not.
 */
export const revenueCatUrl = (a: AmbassadorAccess): string | null =>
  a.uid && a.project
    ? `https://app.revenuecat.com/projects/${a.project.replace(/^proj/, '')}/customers/${encodeURIComponent(a.uid)}`
    : null

const DAY = 86_400_000
/** Lara asked to be warned in time: a month before a grant ends. */
export const EXPIRING_DAYS = 30

export type AccessState = 'active' | 'expiring' | 'none' | 'no-account' | 'unknown'

export function accessState(a: AmbassadorAccess | undefined, now = Date.now()): AccessState {
  if (!a || (a.pending && !a.checkedAt)) return 'unknown'
  if (a.premium === 'no-account') return 'no-account'
  if (a.premium === 'none') return 'none'
  if (a.untilMs !== null && a.untilMs - now < EXPIRING_DAYS * DAY) return 'expiring'
  return 'active'
}

export const ACCESS_LABEL: Record<AccessState, string> = {
  active: 'Premium',
  expiring: 'Expiring soon',
  none: 'No Premium',
  'no-account': 'No app account',
  unknown: 'Not checked',
}

const fmt = (ms: number) => new Date(ms).toLocaleDateString('nb-NO', { day: 'numeric', month: 'short', year: 'numeric' })

/** One line for the table and the card. */
export function accessText(a: AmbassadorAccess | undefined, now = Date.now()): string {
  const s = accessState(a, now)
  if (a?.pending && !a.checkedAt) return 'Checking with RevenueCat…'
  if (!a || s === 'unknown') return 'Not checked'
  if (s === 'no-account') return 'No app account'
  if (s === 'none') return 'No Premium'
  if (a.premium === 'lifetime') return 'Premium · lifetime'
  const kind = a.premium === 'subscription' ? 'Subscribes' : 'Granted'
  const days = Math.ceil((a.untilMs! - now) / DAY)
  return `${kind} until ${fmt(a.untilMs!)}${s === 'expiring' ? ` · ${days} d left` : ''}`
}
